"use client";
import { Suspense, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Search, RefreshCw, ArrowRight, ShieldCheck, KeyRound } from "lucide-react";
import { RequireRole, roleLabel } from "../console-shell";
import { useSession } from "../../lib/session";
import { formatTime } from "../../lib/api";
import { Banner, Button, Desk, PageHeader } from ".";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { ReadState, useRead } from "./record-tools";
import { AccessNav, GrantReading, GrantStatus } from "./access-reading";
import { AccessEditorForm } from "./access-editor";
import {
  BASIS_LABEL,
  GRANT_LABEL,
  grantState,
  scopeText,
  POST_LABEL,
  type AccessEditor,
  type Authority,
  type Gap
} from "./access-model";
export function GrantsWorkspace({ own = false }: { own?: boolean }) {
  const view = (
    <Suspense fallback={<Desk>正在读取授权…</Desk>}>
      <GrantDesk own={own} />
    </Suspense>
  );
  return own ? view : <RequireRole roleCode="ACCESS_ADMIN">{view}</RequireRole>;
}
function GrantDesk({ own }: { own: boolean }) {
  const params = useSearchParams(),
    { refresh, hasRole } = useSession();
  const [tab, setTab] = useState(own ? "mine" : "records"),
    [status, setStatus] = useState(""),
    [word, setWord] = useState(""),
    [q, setQ] = useState("");
  const [account, setAccount] = useState(own ? "" : (params.get("account") ?? "")),
    [page, setPage] = useState(0),
    [selected, setSelected] = useState<string | null>(params.get("record")),
    [editor, setEditor] = useState<AccessEditor | null>(null),
    [message, setMessage] = useState(""),
    [now, setNow] = useState(Date.now());
  const focus = useRef<HTMLElement | null>(null),
    heading = useRef<HTMLHeadingElement>(null),
    opened = useRef("");
  const gapId = params.get("gap");
  const read = useRead<Authority>(
    own
      ? "/api/v1/grants/mine"
      : `/api/v1/grants/workspace${account ? `?accountId=${encodeURIComponent(account)}` : ""}`
  );
  const gaps = useRead<{ items: Gap[] }>(own ? null : "/api/v1/grants/appointment-gaps");
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    const id = params.get("record");
    if (id) setSelected(id);
  }, [params]);
  useEffect(() => {
    const gap = gaps.data?.items.find((g) => g.appointmentId === gapId);
    if (gap && opened.current !== `${gapId}:${account}`) {
      opened.current = `${gapId}:${account}`;
      setEditor({ kind: "grant", action: "issue", gap, accountId: account || gap.accountId || "" });
    }
  }, [gaps.data, gapId, account]);
  const ownItems = read.data?.items ?? [],
    outgoing = read.data?.delegations ?? [];
  const all = own ? [...ownItems, ...outgoing] : ownItems,
    chosen = all.find((g) => g.id === selected);
  const base = own && tab === "outgoing" ? outgoing : ownItems;
  const filtered = base.filter(
    (g) =>
      (!status || grantState(g, now) === status) &&
      (!q ||
        `${g.accountName} ${roleLabel(g.roleCode)} ${scopeText(g)} ${g.issuerName ?? ""}`
          .toLowerCase()
          .includes(q.toLowerCase()))
  );
  const currentPage = Math.min(page, Math.max(0, Math.ceil(filtered.length / 20) - 1));
  const rows = filtered.slice(currentPage * 20, (currentPage + 1) * 20);
  const open = (id: string) => {
    focus.current = document.activeElement as HTMLElement;
    setSelected(id);
  };
  if (editor)
    return (
      <Desk>
        <AccessEditorForm
          editor={editor}
          onCancel={() => setEditor(null)}
          onSaved={(result) => {
            const action = editor;
            setEditor(null);
            setNow(Date.now());
            setPage(0);
            if (action.kind === "password") {
              void refresh();
              return;
            }
            const id = (result as { id?: string }).id;
            if (id) setSelected(id);
            setMessage(
              action.action === "issue"
                ? "授权已签发，等待另一位授权管理员复核。"
                : action.action === "review"
                  ? "复核已完成，授权按约定生效时间启用。"
                  : action.action === "delegate"
                    ? "转授已登记，请在有效期内办理。"
                    : action.action === "shorten" ? "有效期已缩短，正在读取最新记录。" : "授权已撤销，正在读取最新记录。"
            );
            if (action.action === "issue") setTab("records");
            if (action.action === "delegate") setTab("outgoing");
            void read.reload();
            if (!own) void gaps.reload();
            void refresh();
          }}
        />
      </Desk>
    );
  return (
    <Desk wide>
      <PageHeader
        eyebrow={own ? "账号与授权 / 我的工作范围" : "账号与授权 / 职责与依据"}
        title={own ? "我的授权" : "授权管理"}
        description={
          own
            ? "看清自己能办的事项，以及转授出去的工作范围。"
            : "按人员核对职责、组织范围和授权依据，分别办理签发、复核与撤销。"
        }
        actions={
          own ? (
            <Button
              variant="outline"
              onClick={() => setEditor({ kind: "password", action: "password" })}
            >
              <KeyRound size={16} />
              修改口令
            </Button>
          ) : hasRole("ACCESS_ADMIN") ? <Button onClick={() => setEditor({ kind: "grant", action: "issue" })}>签发授权</Button> : undefined
        }
      />
      <AccessNav current={own ? "authority" : "grants"} />
      {message && <Banner tone="success">{message}</Banner>}
      {gapId && tab !== "gaps" && <ReadState {...gaps} retry={gaps.reload} />}
      {own && read.data && (
        <div className="access-overview">
          <div>
            <span>我的生效授权</span>
            <strong>{ownItems.filter((g) => grantState(g, now) === "ACTIVE").length}</strong>
            <small>在约定组织范围内办理</small>
          </div>
          <div>
            <span>我的待复核授权</span>
            <strong>{ownItems.filter((g) => grantState(g, now) === "PENDING").length}</strong>
            <small>复核前暂不生效</small>
          </div>
          <div>
            <span>生效中的转授</span>
            <strong>{outgoing.filter((g) => grantState(g, now) === "ACTIVE").length}</strong>
            <small>可查去向与到期时间</small>
          </div>
        </div>
      )}
      <div className="record-tabs access-tabs" aria-label="授权记录分类">
        {(own
          ? [
              ["mine", "我获得的授权"],
              ["outgoing", "我转授出去的"]
            ]
          : [
              ["records", "授权记录"],
              ["gaps", "任职待授清单"]
            ]
        ).map(([id, label]) => (
          <button
            key={id}
            aria-current={tab === id ? "page" : undefined}
            onClick={() => {
              setTab(id);
              setStatus("");
              setPage(0);
              setQ("");
              setWord("");
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "gaps" ? (
        <>
          <ReadState {...gaps} retry={gaps.reload} />
          {gaps.data && (
            <>
              <div className="record-list-meta">
                <span>本次读取 {gaps.data.items.length} 项 · 最多 500 项</span>
                <span>由现届任职与已有授权派生</span>
              </div>
              {gaps.data.items.length ? (
                <div className="record-table-scroll">
                  <table className="record-table access-table">
                    <caption className="sr-only">任职待授事项</caption>
                    <thead>
                      <tr>
                        <th>任职人员</th>
                        <th>组织与届次</th>
                        <th>接收账号</th>
                        <th>办理</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gaps.data.items.map((g) => (
                        <tr key={g.appointmentId}>
                          <td>
                            <strong>{g.personName}</strong>
                            <small>{POST_LABEL[g.postCode] ?? g.postCode}</small>
                          </td>
                          <td>
                            {g.organizationName}
                            <small>{g.termName}</small>
                          </td>
                          <td>{g.accountId ? "已有关联账号" : "尚未关联账号"}</td>
                          <td>
                            <div className="workspace-actions">
                              {!g.accountId && (
                                <Link
                                  className="record-link"
                                  href={`/accounts?gap=${encodeURIComponent(g.appointmentId)}`}
                                >
                                  开通账号
                                  <ArrowRight size={14} />
                                </Link>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setEditor({ kind: "grant", action: "issue", gap: g })
                                }
                              >
                                核对并签发
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="workspace-empty">
                  <ShieldCheck />
                  <strong>本次读取没有任职待授事项</strong>
                  <p>现届任职和已有签发记录变化后，可重新读取。</p>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {account && (
            <div className="access-context">
              <span>正在查看指定账号的授权{base[0] ? `：${base[0].accountName}` : ""}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setAccount("");
                  setPage(0);
                }}
              >
                查看全部账号
              </Button>
            </div>
          )}
          <div className="access-filters" aria-label="授权状态筛选">
            {[["", "全部"], ...Object.entries(GRANT_LABEL)].map(([id, label]) => (
              <button
                key={id}
                aria-pressed={status === id}
                onClick={() => {
                  setStatus(id);
                  setPage(0);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="record-toolbar">
            <form
              className="record-search"
              onSubmit={(e) => {
                e.preventDefault();
                setQ(word.trim());
                setPage(0);
              }}
            >
              <Search size={17} />
              <input
                aria-label="搜索人员、职责或组织"
                placeholder="搜索人员、职责或组织"
                value={word}
                onChange={(e) => setWord(e.target.value)}
              />
              <Button type="submit" variant="outline">
                搜索
              </Button>
            </form>
            <Button
              variant="ghost"
              aria-label="刷新授权"
              onClick={() => {
                void read.reload();
                void refresh();
              }}
            >
              <RefreshCw size={16} />
            </Button>
          </div>
          <ReadState {...read} retry={read.reload} />
          {read.data && (
            <>
              <div className="record-list-meta">
                <span>
                  匹配 {filtered.length} 条 · 当前页 {rows.length} 条
                </span>
                <span>本次读取 {base.length} 条授权记录</span>
              </div>
              {rows.length ? (
                <div className="record-table-scroll">
                  <table className="record-table access-table">
                    <caption className="sr-only">授权记录</caption>
                    <thead>
                      <tr>
                        <th>{own && tab === "mine" ? "职责与依据" : "人员与职责"}</th>
                        <th>组织范围</th>
                        <th>当前状态</th>
                        <th>有效期</th>
                        <th>
                          <span className="sr-only">详情</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((g) => (
                        <tr key={g.id}>
                          <td>
                            <button className="access-record" onClick={() => open(g.id)}>
                              <ShieldCheck size={18} />
                              <span>
                                <strong>
                                  {own && tab === "mine" ? roleLabel(g.roleCode) : g.accountName}
                                </strong>
                                <small>
                                  {own && tab === "mine"
                                    ? BASIS_LABEL[g.grantBasis]
                                    : roleLabel(g.roleCode)}
                                  {g.accountStatus === "DISABLED" ? " · 账号已停用" : ""}
                                </small>
                              </span>
                            </button>
                          </td>
                          <td className="access-scope">
                            {g.scopeOrganizations.map((o) => o.name).join("、")}
                            <small>
                              {g.scopeMode === "SUBTREE_DYNAMIC" ? "包含下级组织" : "仅所列组织"}
                            </small>
                          </td>
                          <td>
                            <GrantStatus grant={g} now={now} />
                          </td>
                          <td>
                            {g.validTo ? `${formatTime(g.validTo)} 到期` : "未设到期时间"}
                            <small>{formatTime(g.validFrom)} 起</small>
                          </td>
                          <td>
                            {!own && hasRole("ACCESS_ADMIN") && grantState(g, now) === "ACTIVE" && <Button size="sm" variant="outline" onClick={() => setEditor({ kind: "grant", action: "shorten", record: g })}>缩短有效期</Button>}
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`查看${g.accountName}的${roleLabel(g.roleCode)}授权`}
                              onClick={() => open(g.id)}
                            >
                              <ArrowRight size={15} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="workspace-empty">
                  <ShieldCheck />
                  <strong>
                    {base.length
                      ? "没有匹配的授权"
                      : own && tab === "outgoing"
                        ? "还没有转授记录"
                        : "尚未读取到授权记录"}
                  </strong>
                  <p>
                    {own && tab === "mine"
                      ? "授权管理员签发并完成复核后，即可按范围办理。"
                      : "可调整筛选或重新读取。"}
                  </p>
                </div>
              )}
              <div className="record-pagination">
                <span>第 {currentPage + 1} 页</span>
                <Button
                  variant="outline"
                  disabled={currentPage === 0}
                  onClick={() => setPage(currentPage - 1)}
                >
                  上一页
                </Button>
                <Button
                  variant="outline"
                  disabled={(currentPage + 1) * 20 >= filtered.length}
                  onClick={() => setPage(currentPage + 1)}
                >
                  下一页
                </Button>
              </div>
            </>
          )}
        </>
      )}
      {gapId && gaps.data && !gaps.data.items.some((g) => g.appointmentId === gapId) && (
        <p className="record-muted">该待授事项已不在当前清单中，可在授权记录中核对已签发结果。</p>
      )}
      <h2 className="sr-only" ref={heading} tabIndex={-1}>
        授权工作区
      </h2>
      <Sheet
        open={!!selected}
        onOpenChange={(v) => {
          if (!v) setSelected(null);
        }}
      >
        <SheetContent
          className="record-sheet access-sheet workspace"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            (focus.current?.isConnected ? focus.current : heading.current)?.focus();
          }}
        >
          <SheetHeader>
            <p className="record-eyebrow">职责与授权依据</p>
            <SheetTitle>
              {chosen ? `${chosen.accountName} · ${roleLabel(chosen.roleCode)}` : "读取授权详情"}
            </SheetTitle>
            <SheetDescription>核对这条授权的范围、有效期和办理记录。</SheetDescription>
          </SheetHeader>
          <ReadState {...read} retry={read.reload} />
          {chosen ? (
            <GrantReading key={chosen.id} grant={chosen} own={own} onAction={setEditor} />
          ) : (
            read.data && (
              <p className="record-muted access-not-found">
                这条授权不在当前可读范围内，请关闭后重新查询。
              </p>
            )
          )}
        </SheetContent>
      </Sheet>
    </Desk>
  );
}
